import { FirebaseError } from '@firebase/app';

// Firebase auth-specific friendly messages
const firebaseErrorMessages: Record<string, string> = {
  'auth/email-already-in-use': 'This email is already registered.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/operation-not-allowed': 'This operation is not allowed.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password.',
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/popup-blocked':
    'Your browser blocked the sign-in window. Allow popups for this site and try again.',
  'auth/credential-already-in-use':
    'That Google account is already connected to another REDI account.',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email. Sign in with your original method first.',
  'auth/operation-not-supported-in-this-environment':
    'Google sign-in is only available on the web app for now.',
  // Firestore codes are unprefixed (unlike auth/*). permission-denied became
  // user-reachable once rules started enforcing post visibility.
  'permission-denied': "You don't have access to this content.",
  'failed-precondition': 'This view needs a server update. Please try again shortly.',
  unavailable: 'Network error. Please check your connection.',
};

export type AppErrorCode =
  | 'UNKNOWN'
  | 'VALIDATION_ERROR'
  | 'FIRESTORE_ERROR'
  | 'AUTH_ERROR'
  | 'NETWORK_ERROR'
  | 'NOT_FOUND'
  | 'PERMISSION_DENIED'
  | 'RATE_LIMITED';

export class AppError extends Error {
  constructor(
    message: string,
    public readonly code: AppErrorCode,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'AppError';
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  static isCode(error: unknown, code: AppErrorCode): error is AppError {
    return error instanceof AppError && error.code === code;
  }
}

export function wrapError(
  error: unknown,
  context: string,
  code: AppErrorCode = 'UNKNOWN'
): AppError {
  const originalMessage = getErrorMessage(error);
  return new AppError(`${context}: ${originalMessage}`, code, error);
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    return firebaseErrorMessages[error.code] || error.message;
  }
  if (error instanceof AppError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred.';
}

export function isPermissionDenied(error: unknown): boolean {
  return (
    (error instanceof FirebaseError && error.code === 'permission-denied') ||
    AppError.isCode(error, 'PERMISSION_DENIED')
  );
}

export function isFirebaseAuthError(error: unknown, authCode: string): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code: string }).code === authCode
  );
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('timeout') ||
      message.includes('offline')
    );
  }
  return false;
}
